import { CleanHtml, cleanHtmlDecodeString, cleanHtmlString, replaceEntities } from '@components/cleanHtml';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { IntroductionProps } from './types';

const Introduction: React.FC<IntroductionProps> = (props) => {
  const [expanded, setExpanded] = useState(false);
  const [isTruncable, setIsTruncable] = useState(false);
  const truncateRef = useRef<HTMLDivElement>(null);

  const checkTruncation = useCallback(() => {
    const el = truncateRef.current;
    if (el) {
      setIsTruncable(el.scrollHeight > el.clientHeight);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [checkTruncation]);

  const toggleLines = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const renderTitle = () => {
    if (!props.component.title) {
      return null;
    }

    let renderTitleContent: any = props.component.title;
    if (props.nonBreakingTitleBraces) {
      const match = renderTitleContent.match(/^(.+?)(\(.+?\))$/);
      if (match) {
        renderTitleContent = (
          <span>
            <span>{match[1]}</span>
            <span className="whitespace-nowrap text-[0.9em]">{match[2]}</span>
          </span>
        );
      }
    }

    return <h2 className="Base-title mb-10 w-full max-w-full text-[40px] leading-[1.1] font-light max-[600px]:text-[32px] max-[600px]:leading-10 font-heading">{renderTitleContent}</h2>;
  };

  const renderSubHeadline = () => {
    const shortText = (props.component.shorttext || '').trim();
    if (!shortText) {
      return null;
    }

    const decodedEntities = replaceEntities(shortText);

    return (
      <div className="w-full font-heading text-2xl leading-[1.3] text-black max-[700px]:text-base max-[700px]:leading-6 max-[1100px]:mt-[60px]">
        <span>{decodedEntities}</span>
      </div>
    );
  };

  const renderText = () => {
    const warning = props.component.warning ? `\n<p><strong>${props.component.warning}</strong></p>` : '';
    const longText = (props.component.longtext || '').trim();
    const textClasses = `font-[Arial] text-base leading-[26px] text-black mb-2.5 overflow-hidden${props.component.warning ? ' [&_p:last-child]:mt-4' : ''}`;

    if (props.forceLongText) {
      return <CleanHtml className={textClasses} html={longText + warning} />;
    }
    const truncable = isTruncable || !!warning;

    if (!longText && !warning) {
      return null;
    }

    const cleanLongText = cleanHtmlDecodeString(
      cleanHtmlString({
        html: longText,
        allowedTags: [],
      }),
    );

    return (
      <div className={textClasses}>
        <div ref={truncateRef} className={`whitespace-pre-wrap ${expanded ? 'pointer-events-none invisible absolute z-0' : 'line-clamp-3'}`}>
          {cleanLongText}
        </div>

        {expanded ? <CleanHtml className={textClasses} html={longText + warning} /> : null}

        <div className={`mb-2.5 flex items-center justify-center transition-[min-height] delay-500 duration-500 ease-in-out ${truncable ? 'min-h-[50px]' : 'min-h-5'}`}>
          {truncable ? (
            <img src={`/static/front/assets/other-icons/block-${expanded ? 'less' : 'more'}.svg`} onClick={toggleLines} width="51" height="27" alt={expanded ? 'Show less' : 'Show more'} className="cursor-pointer" />
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="Introduction mb-0 w-full">
      {renderTitle()}
      {renderSubHeadline()}
      {renderText()}
    </div>
  );
};

export { Introduction };
